import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import {
    LayoutDashboard,
    Upload,
    History,
    FileText,
    CreditCard,
    Settings as SettingsIcon,
    Sparkles,
    Search,
    Bell,
    LogOut,
    Moon,
    Sun,
    ChevronRight,
    Menu,
    X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/components/ThemeProvider";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/dashboard/upload", label: "Upload Tender", icon: Upload },
    { to: "/dashboard/history", label: "Analysis History", icon: History },
    { to: "/dashboard/reports", label: "Reports", icon: FileText },
    { to: "/dashboard/billing", label: "Billing", icon: CreditCard },
    { to: "/dashboard/settings", label: "Settings", icon: SettingsIcon },
];

function SidebarContent({ onNavigate, profile, onLogout }) {
    const credits = profile?.credits ?? 0;
    const plan = profile?.plan === "free" ? "Free" : profile?.plan ?? "Free";
    const planCap = plan === "Free" ? 3 : plan === "Pro" ? 30 : plan === "Business" ? 999 : 10;
    const usagePct = Math.min(100, ((planCap - credits) / planCap) * 100);
    const initials = (profile?.full_name || profile?.email || "U")
        .split(" ")
        .map((s) => s[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    return (
        <div className="flex h-full flex-col">
            <Link
                to="/"
                className="flex items-center gap-2 px-2 h-16 border-b border-slate-200 dark:border-slate-800"
            >
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/30">
                    <Sparkles className="h-5 w-5 text-white" />
                </div>
                <span className="font-bold tracking-tight text-slate-900 dark:text-slate-50">
                    TenderTrackAI
                </span>
            </Link>

            <nav className="flex-1 px-3 py-6 space-y-1">
                {NAV.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                            cn(
                                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                                isActive
                                    ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300"
                                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900 hover:text-slate-900 dark:hover:text-slate-100",
                            )
                        }
                        data-testid={`sidebar-${item.label.toLowerCase().replace(/ /g, "-")}`}
                    >
                        <item.icon className="h-4 w-4 shrink-0" />
                        {item.label}
                    </NavLink>
                ))}
            </nav>

            <div className="px-3 pb-3">
                <div className="rounded-xl border border-indigo-200 dark:border-indigo-900/60 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/40 p-4">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                            Credits Remaining
                        </span>
                        <Badge className="bg-indigo-600 text-white hover:bg-indigo-600 text-[10px] capitalize">
                            {plan}
                        </Badge>
                    </div>
                    <div className="flex items-baseline gap-1 mb-2">
                        <span className="text-xl font-bold font-mono text-slate-900 dark:text-slate-50">
                            {credits}
                        </span>
                        <span className="text-sm text-slate-500">
                            {" "}/ {planCap}
                        </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white dark:bg-slate-900 overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full"
                            style={{ width: `${usagePct}%` }}
                        />
                    </div>
                    <Link to="/dashboard/billing">
                        <Button
                            size="sm"
                            className="mt-3 w-full h-8 bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 dark:text-slate-900 text-xs"
                            data-testid="sidebar-upgrade-btn"
                        >
                            Upgrade Plan
                        </Button>
                    </Link>
                </div>
            </div>

            <div className="border-t border-slate-200 dark:border-slate-800 p-3">
                <div className="flex items-center gap-3 px-2 py-2">
                    <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-indigo-600 text-white text-xs font-semibold">
                            {initials}
                        </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate text-slate-900 dark:text-slate-100">
                            {profile?.full_name || "User"}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                            {profile?.email}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (typeof onLogout === "function") onLogout();
                        }}
                        aria-label="Log out"
                        data-testid="sidebar-logout-btn"
                        className="text-slate-400 hover:text-red-500 cursor-pointer"
                    >
                        <LogOut className="h-4 w-4 pointer-events-none" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function DashboardHeader({ crumb, mobileOpen, setMobileOpen, theme, toggle }) {
    return (
        <header className="sticky top-0 z-20 h-16 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
            <div className="h-full px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                    <button
                        className="lg:hidden h-9 w-9 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-800"
                        onClick={() => setMobileOpen(true)}
                        data-testid="dashboard-mobile-menu-btn"
                    >
                        {mobileOpen ? (
                            <X className="h-5 w-5" />
                        ) : (
                            <Menu className="h-5 w-5" />
                        )}
                    </button>
                    <div className="hidden sm:flex items-center gap-2 text-sm text-slate-500">
                        <span>Dashboard</span>
                        {crumb && (
                            <>
                                <ChevronRight className="h-4 w-4" />
                                <span className="text-slate-900 dark:text-slate-100 capitalize font-medium">
                                    {crumb || "Overview"}
                                </span>
                            </>
                        )}
                    </div>
                </div>

                <div className="flex-1 max-w-md hidden md:block">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Search tenders, departments..."
                            className="pl-9 h-9 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                            data-testid="dashboard-search-input"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={toggle}
                        className="h-9 w-9 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900"
                        data-testid="dashboard-theme-toggle"
                    >
                        {theme === "dark" ? (
                            <Sun className="h-4 w-4" />
                        ) : (
                            <Moon className="h-4 w-4" />
                        )}
                    </button>
                    <button className="relative h-9 w-9 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900">
                        <Bell className="h-4 w-4" />
                        <span className="absolute top-2 right-2 h-1.5 w-1.5 bg-indigo-600 rounded-full" />
                    </button>
                    <Link to="/dashboard/upload">
                        <Button
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-500/25 h-9 hidden sm:flex"
                            data-testid="header-upload-btn"
                        >
                            <Upload className="h-4 w-4 mr-2" /> New Analysis
                        </Button>
                    </Link>
                </div>
            </div>
        </header>
    );
}

export default function DashboardLayout() {
    const { theme, toggle } = useTheme();
    const { profile, signOut } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = async () => {
        try {
            await signOut();
            toast.success("Logged out");
            navigate("/login", { replace: true });
        } catch (err) {
            toast.error(err.message || "Logout failed");
        }
    };

    const crumb = location.pathname
        .replace("/dashboard", "")
        .replace("/", "")
        .replace(/-/g, " ");

    return (
        <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
            <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 z-30">
                <SidebarContent profile={profile} onLogout={handleLogout} />
            </aside>

            {mobileOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-sm"
                    onClick={() => setMobileOpen(false)}
                />
            )}
            <aside
                className={cn(
                    "lg:hidden fixed inset-y-0 left-0 w-72 bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 z-50 transition-transform duration-300",
                    mobileOpen ? "translate-x-0" : "-translate-x-full",
                )}
            >
                <SidebarContent
                    onNavigate={() => setMobileOpen(false)}
                    profile={profile}
                    onLogout={handleLogout}
                />
            </aside>

            <div className="lg:pl-64">
                <DashboardHeader
                    crumb={crumb}
                    mobileOpen={mobileOpen}
                    setMobileOpen={setMobileOpen}
                    theme={theme}
                    toggle={toggle}
                />

                <main className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
